/**
 * Moisture and land-terrain classes.
 *
 * Moisture = broad fBm climate + a prevailing-wind gradient + proximity to sea/lakes, raised along
 * rivers once they exist. Terrain classes are assigned by *quota*: each map draws a target mix
 * (plains ≈ 50 %, forest ≈ 22 %, hills ≈ 13 %, mountains ≈ 6 %, marsh ≈ 4.5 %) and the highest-
 * scoring land hexes for each class take it, in order mountains → hills → marsh → forest. Scores
 * come from smooth fields (elevation, moisture, low-frequency noise), so every class forms coherent
 * patches: ranges, hill belts, river fens and woods rather than salt-and-pepper.
 */
import type { Rng } from '../core/rng';
import { Terrain } from '../core/types';
import { clamp, isLand, landHexes, type MapContext, topByScore } from './context';
import { Noise } from './noise';
import { bfs } from './paths';

export function computeMoisture(ctx: MapContext, rng: Rng): void {
  const { grid, n, terrain, elevation, moisture, ux, uy } = ctx;
  const minDim = Math.min(ctx.extentX, ctx.extentY);
  const climate = new Noise(rng);
  let wx = rng.gaussian();
  let wy = rng.gaussian();
  const norm = Math.sqrt(wx * wx + wy * wy) || 1;
  wx /= norm;
  wy /= norm;
  const water: number[] = [];
  const landMask = new Uint8Array(n);
  for (let h = 0; h < n; h++) {
    if (isLand(terrain[h])) landMask[h] = 1;
    else water.push(h);
  }
  const dist = bfs(grid, water, landMask).dist;
  const f = 2.5 / minDim;
  for (let h = 0; h < n; h++) {
    if (!landMask[h]) {
      moisture[h] = 1;
      continue;
    }
    // Wind gradient: the windward side of the continent (relative position along wind) is wetter.
    const wind = -((ux[h] / ctx.extentX - 0.5) * wx + (uy[h] / ctx.extentY - 0.5) * wy);
    const shore = 0.3 / (1 + 0.45 * (dist[h] - 1));
    const highland = 0.08 * clamp((elevation[h] - 120) / 100, 0, 1);
    moisture[h] = clamp(
      0.4 + 0.25 * climate.fbm(ux[h] * f, uy[h] * f, 4) + 0.2 * wind + shore + highland,
      0,
      1,
    );
  }
}

/** River banks are wetter (bank +0.15, one hex further +0.06). */
export function addRiverMoisture(ctx: MapContext): void {
  const { grid, n, terrain, riverEdges, moisture } = ctx;
  const bonus = new Float64Array(n);
  for (let h = 0; h < n; h++) {
    if (!riverEdges[h]) continue;
    bonus[h] = 0.15;
    grid.forNeighbours(h, (m) => {
      if (bonus[m] < 0.06) bonus[m] = 0.06;
    });
  }
  for (let h = 0; h < n; h++) if (isLand(terrain[h])) moisture[h] = clamp(moisture[h] + bonus[h], 0, 1);
}

/** Target shares of land per class, drawn per map around the design mix. */
function drawMix(rng: Rng): { forest: number; hills: number; mountains: number; marsh: number } {
  const nonPlains = rng.range(0.46, 0.51);
  const f = 0.44 * rng.range(0.88, 1.12);
  const h = 0.26 * rng.range(0.88, 1.12);
  const m = 0.12 * rng.range(0.85, 1.15);
  const s = 0.09 * rng.range(0.85, 1.15);
  const k = nonPlains / (f + h + m + s);
  return { forest: f * k, hills: h * k, mountains: m * k, marsh: s * k };
}

export function classifyTerrain(ctx: MapContext, rng: Rng): void {
  const { grid, n, terrain, elevation, moisture, riverEdges, ux, uy } = ctx;
  const land = landHexes(ctx);
  const L = land.length;
  const mix = drawMix(rng);
  const hillNoise = new Noise(rng);
  const marshNoise = new Noise(rng);
  const woodNoise = new Noise(rng);
  const free = (h: number) => terrain[h] === Terrain.Plains;

  // Mountains: the highest ground, which the spines concentrate into chains.
  for (const h of topByScore(land, elevation, Math.round(L * mix.mountains))) terrain[h] = Terrain.Mountains;
  // A lone mountain hex reads as noise, not a range: demote to hills.
  let hills = 0;
  for (const h of land) {
    if (terrain[h] !== Terrain.Mountains) continue;
    let linked = false;
    grid.forNeighbours(h, (m) => {
      if (terrain[m] === Terrain.Mountains) linked = true;
    });
    if (!linked) {
      terrain[h] = Terrain.Hills;
      hills++;
    }
  }

  // Hills: high or rugged ground — the flanks of ranges plus upland plateaus.
  const score = new Float64Array(n);
  for (const h of land) {
    let relief = 0;
    grid.forNeighbours(h, (m) => {
      if (isLand(terrain[m])) relief = Math.max(relief, Math.abs(elevation[h] - elevation[m]));
    });
    score[h] = elevation[h] + 0.5 * relief + 8 * hillNoise.fbm(ux[h] * 0.25, uy[h] * 0.25, 2);
  }
  const hillQuota = Math.round(L * mix.hills) - hills;
  for (const h of topByScore(land.filter(free), score, hillQuota)) terrain[h] = Terrain.Hills;

  // Marsh: low, wet ground along rivers, lake shores and coasts.
  const plainsElev = land.filter(free).map((h) => elevation[h]);
  plainsElev.sort((a, b) => a - b);
  const lowCut = plainsElev[Math.floor(plainsElev.length * 0.55)] ?? 0;
  const marshCandidates: number[] = [];
  for (const h of land) {
    if (!free(h) || elevation[h] > lowCut) continue;
    let shore = 0;
    grid.forNeighbours(h, (m) => {
      if (!isLand(terrain[m])) shore = 1;
    });
    score[h] =
      moisture[h] +
      (riverEdges[h] ? 0.35 : 0) +
      0.25 * shore -
      0.006 * (elevation[h] - 66) +
      0.35 * marshNoise.fbm(ux[h] * 0.15, uy[h] * 0.15, 3);
    marshCandidates.push(h);
  }
  for (const h of topByScore(marshCandidates, score, Math.round(L * mix.marsh))) terrain[h] = Terrain.Marsh;

  // Forest: large woods from low-frequency noise, favouring moist and slightly raised ground.
  const forestCandidates = land.filter(free);
  for (const h of forestCandidates) {
    score[h] =
      0.75 * woodNoise.fbm(ux[h] * 0.11, uy[h] * 0.11, 4) +
      0.4 * moisture[h] +
      0.12 * clamp((elevation[h] - 80) / 60, 0, 1);
  }
  for (const h of topByScore(forestCandidates, score, Math.round(L * mix.forest)))
    terrain[h] = Terrain.Forest;

  // One smoothing pass: lone trees become fields, clearings deep inside woods become forest.
  const flips: [number, number][] = [];
  for (const h of land) {
    if (terrain[h] !== Terrain.Forest && terrain[h] !== Terrain.Plains) continue;
    let woods = 0;
    grid.forNeighbours(h, (m) => {
      if (terrain[m] === Terrain.Forest) woods++;
    });
    if (terrain[h] === Terrain.Forest && woods === 0) flips.push([h, Terrain.Plains]);
    else if (terrain[h] === Terrain.Plains && woods >= 5) flips.push([h, Terrain.Forest]);
  }
  for (const [h, t] of flips) terrain[h] = t;
}
